import {
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, take, takeUntil, timeout } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { IesSummary } from '../../core/models/ies-summary.model';
import { ReportService } from '../../core/services/report.service';

@Component({
    selector: 'app-application-admins-report-dialog',
    standalone: true,
    imports: [
        CommonModule, FormsModule, MatDialogModule,
        MatButtonModule, MatIconModule, MatProgressSpinnerModule
    ],
    template: `
    <h2 mat-dialog-title>Audit des applications par IES</h2>

    <mat-dialog-content>
      <p class="hint">
        Sélectionnez une IES pour télécharger la liste des applications
        et, pour chacune, les administrateurs (utilisateurs de l'IES ayant un rôle
        sur l'application).
      </p>

      <div class="search-bar">
        <mat-icon>search</mat-icon>
        <!--
          IMPORTANT : pas de (ngModelChange) — le getter computed filteredIes
          se recalcule tout seul à chaque cycle de CD (via le binding [(ngModel)]).
        -->
        <input
          type="text"
          [(ngModel)]="search"
          placeholder="Rechercher une IES (nom, sigle, code)…"
          autocomplete="off" />
        @if (search) {
          <button class="clear-btn" (click)="clearSearch()" title="Effacer">
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>

      <!-- Message d'erreur (proxy, réseau, backend) -->
      @if (errorMessage) {
        <div class="error-banner">
          <mat-icon>error_outline</mat-icon>
          <span>{{ errorMessage }}</span>
        </div>
      }

      @if (loading) {
        <div class="loading"><mat-spinner diameter="32"></mat-spinner></div>
      } @else if (filteredIes.length === 0) {
        <div class="empty">
          @if (allIes.length === 0) {
            Aucune IES n'est configurée dans le système.
          } @else {
            Aucune IES ne correspond à « {{ search }} ».
          }
        </div>
      } @else {
        <div class="ies-list">
          @for (ies of filteredIes; track ies.id) {
            <div class="ies-row">
              <div class="ies-info">
                <div class="ies-name">{{ ies.displayName }}</div>
              </div>

              <button
                type="button"
                class="download-btn"
                [class.is-downloading]="downloading.has(ies.code)"
                [class.is-success]="success.has(ies.code)"
                [disabled]="downloading.has(ies.code)"
                (click)="download(ies)">
                @if (downloading.has(ies.code)) {
                  <span class="btn-spinner" aria-hidden="true"></span>
                  <span class="btn-label">Génération…</span>
                } @else if (success.has(ies.code)) {
                  <mat-icon class="btn-icon">check_circle</mat-icon>
                  <span class="btn-label">Téléchargé</span>
                } @else {
                  <mat-icon class="btn-icon">download</mat-icon>
                  <span class="btn-label">Télécharger</span>
                }
              </button>
            </div>
          }
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="close()">Fermer</button>
    </mat-dialog-actions>
  `,
    styles: [`
    .hint { color: #64748b; font-size: 13px; margin: 0 0 16px; line-height: 1.4; }

    .search-bar {
      display: flex; align-items: center; gap: 8px;
      padding: 8px 12px; margin-bottom: 16px;
      border: 1px solid #e2e8f0; border-radius: 8px;
      background: #fff;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .search-bar:focus-within {
      border-color: #45B56F;
      box-shadow: 0 0 0 3px rgba(59,130,246,0.12);
    }
    .search-bar mat-icon { color: #94a3b8; font-size: 20px; width: 20px; height: 20px; }
    .search-bar input {
      flex: 1; border: none; outline: none; background: transparent;
      font-size: 14px; padding: 4px 0;
    }
    .clear-btn { background: none; border: none; cursor: pointer; padding: 0; color: #94a3b8; }

    .error-banner {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 12px; margin-bottom: 12px;
      background: #fef2f2; color: #b91c1c;
      border: 1px solid #fecaca; border-radius: 8px;
      font-size: 13px;
    }
    .error-banner mat-icon { font-size: 18px; width: 18px; height: 18px; }

    .loading { display: flex; justify-content: center; padding: 32px; }
    .empty { text-align: center; color: #94a3b8; padding: 24px; font-style: italic; }

    .ies-list {
      max-height: 400px; overflow-y: auto;
      border: 1px solid #e2e8f0; border-radius: 10px;
    }
    .ies-row {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 16px; border-bottom: 1px solid #f1f5f9;
      transition: background 0.15s ease;
      gap: 12px;
    }
    .ies-row:last-child { border-bottom: none; }
    .ies-row:hover { background: #f8fafc; }

    .ies-info { flex: 1; min-width: 0; }
    .ies-name { font-weight: 600; color: #1e293b; font-size: 14px; }

    /* ═══════════════════════════════════════════════════════
       BOUTON — natif, 100% contrôlé
       ═══════════════════════════════════════════════════════ */
    .download-btn {
      appearance: none;
      -webkit-appearance: none;
      border: none; outline: none; margin: 0;
      font-family: inherit; cursor: pointer;

      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      flex-shrink: 0;
      box-sizing: border-box;

      min-width: 160px;
      height: 42px;
      padding: 0 20px;
      border-radius: 10px;
      font-size: 13.5px;
      font-weight: 600;
      letter-spacing: 0.2px;
      color: #ffffff;

      background: linear-gradient(135deg, #45B56F 0%, #15753a 100%);
      box-shadow: 0 4px 12px rgba(2, 105, 47, 0.5),
                  0 1px 3px rgba(0,0,0,0.08);
      transition: transform 0.15s ease,
                  box-shadow 0.2s ease,
                  filter 0.2s ease;
      overflow: hidden;
      position: relative;
      user-select: none;
    }

    .download-btn .btn-icon {
      font-size: 19px;
      width: 19px;
      height: 19px;
      line-height: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }

    .download-btn .btn-label {
      white-space: nowrap;
      line-height: 1;
      display: inline-flex;
      align-items: center;
    }

    .download-btn:not(:disabled):hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(2, 105, 47, 0.5),
                  0 2px 6px rgba(0,0,0,0.12);
      filter: brightness(1.08);
    }
    .download-btn:not(:disabled):hover .btn-icon {
      transform: translateY(-1px);
    }
    .download-btn:not(:disabled):active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(2, 105, 47, 0.35);
    }

    .download-btn.is-downloading {
      background: linear-gradient(135deg, #bbd3c4 0%, #45B56F 100%);
      box-shadow: 0 4px 14px rgba(2, 105, 47, 0.35);
      cursor: wait;
    }
    .download-btn.is-downloading::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        100deg,
        transparent 20%,
        rgba(255,255,255,0.28) 50%,
        transparent 80%
      );
      transform: translateX(-100%);
      animation: btn-shimmer 1.4s ease-in-out infinite;
      pointer-events: none;
    }
    @keyframes btn-shimmer {
      to { transform: translateX(100%); }
    }

    .btn-spinner {
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 2.5px solid rgba(255,255,255,0.35);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: btn-spin 0.75s linear infinite;
      flex-shrink: 0;
      box-sizing: border-box;
    }
    @keyframes btn-spin {
      to { transform: rotate(360deg); }
    }

    .download-btn.is-success {
      background: linear-gradient(135deg, #82f0cb 0%, #0bb37d 100%);
      box-shadow: 0 4px 14px rgba(16,185,129,0.45);
    }
    .download-btn.is-success .btn-icon {
      animation: btn-pop 0.35s ease-out;
    }
    @keyframes btn-pop {
      0%   { transform: scale(0.5); }
      60%  { transform: scale(1.15); }
      100% { transform: scale(1); }
    }

    .download-btn:disabled {
      opacity: 1;
      pointer-events: none;
    }
  `]
})
export class ApplicationAdminsReportDialogComponent implements OnInit, OnDestroy {
    allIes: IesSummary[] = [];
    loading = true;
    search = '';
    errorMessage: string | null = null;

    downloading = new Set<string>();
    success = new Set<string>();

    private destroy$ = new Subject<void>();

    constructor(
        private reportService: ReportService,
        private cdr: ChangeDetectorRef,
        private dialogRef: MatDialogRef<ApplicationAdminsReportDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) { }

    // ══════════════════════════════════════════════════════════════════
    //  Getter computé : toujours synchrone avec `search`
    //  (élimine le bug de désynchronisation ngModel)
    // ══════════════════════════════════════════════════════════════════
    get filteredIes(): IesSummary[] {
        const term = this.search.trim().toLowerCase();
        if (!term) return this.allIes;

        return this.allIes.filter(ies =>
            ies.displayName.toLowerCase().includes(term) ||
            (ies.sigle ?? '').toLowerCase().includes(term) ||
            (ies.code ?? '').toLowerCase().includes(term)
        );
    }

    ngOnInit(): void {
        this.reportService.getRootIes()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ies => {
                    this.allIes = [...ies].sort((a, b) =>
                        a.displayName.localeCompare(b.displayName));
                    this.loading = false;
                    this.cdr.detectChanges();
                },
                error: err => {
                    console.error('Erreur chargement IES', err);
                    this.errorMessage =
                        'Impossible de charger la liste des IES. Vérifiez votre connexion.';
                    this.loading = false;
                    this.cdr.detectChanges();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    clearSearch(): void {
        this.search = '';
        // Pas besoin de toucher `filteredIes` : le getter se recalcule au prochain CD
    }

    // ══════════════════════════════════════════════════════════════════
    //  Téléchargement — 5 garde-fous pour garantir l'arrêt du loader
    //    1. take(1)              → force la complétion après 1 émission
    //    2. timeout(120 s)       → borne dure
    //    3. finalize()           → s'exécute TOUJOURS (succès, erreur, unsubscribe)
    //    4. try/catch DOM        → a.click() ne peut pas bloquer
    //    5. detectChanges()      → garantit le refresh de la vue
    // ══════════════════════════════════════════════════════════════════
    download(ies: IesSummary): void {
        if (this.downloading.has(ies.code) || this.success.has(ies.code)) return;

        // ── État "downloading" ────────────────────────────────
        this.errorMessage = null;
        this.downloading = new Set([...this.downloading, ies.code]);
        this.cdr.detectChanges();    // refresh immédiat → spinner visible

        this.reportService
            .downloadApplicationsAdminsReport(ies.code)
            .pipe(
                take(1),                            // force complete après next
                timeout(120_000),                   // sécurité absolue
                finalize(() => {
                    // TOUJOURS exécuté, quel que soit le chemin
                    this.downloading = new Set(
                        [...this.downloading].filter(c => c !== ies.code)
                    );
                    this.cdr.detectChanges();
                }),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: (blob: Blob) => {
                    // ── Vérifie que le backend a bien renvoyé un PDF ─────
                    // (si proxy mal configuré → blob = text/html = index.html)
                    if (blob.type && blob.type.toLowerCase().includes('text/html')) {
                        this.errorMessage =
                            'Le serveur a renvoyé une page HTML au lieu du PDF. ' +
                            'Vérifiez la configuration du proxy backend.';
                        this.cdr.detectChanges();
                        return;
                    }
                    if (blob.size === 0) {
                        this.errorMessage = 'Le fichier généré est vide.';
                        this.cdr.detectChanges();
                        return;
                    }

                    // ── Déclenchement du téléchargement navigateur ───────
                    try {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        const sigle = ies.sigle ?? ies.code;
                        const date = new Date().toISOString().slice(0, 10);
                        a.download = `audit_applications_${sigle}_${date}.pdf`;
                        a.style.display = 'none';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        // Libération différée pour éviter les races sur Safari
                        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                    } catch (e) {
                        console.error('Erreur déclenchement téléchargement', e);
                        this.errorMessage = 'Erreur lors de l\'enregistrement du fichier.';
                        this.cdr.detectChanges();
                        return;
                    }

                    // ── Flash succès 1,8 s ───────────────────────────────
                    this.success = new Set([...this.success, ies.code]);
                    this.cdr.detectChanges();

                    setTimeout(() => {
                        this.success = new Set(
                            [...this.success].filter(c => c !== ies.code)
                        );
                        this.cdr.detectChanges();
                    }, 1800);
                },
                error: (err) => {
                    console.error('Erreur téléchargement', err);
                    this.errorMessage = err?.name === 'TimeoutError'
                        ? 'Le serveur met trop de temps à répondre. Réessayez.'
                        : 'Impossible de générer le rapport. Réessayez plus tard.';
                    // Le spinner est déjà nettoyé par finalize()
                }
            });
    }

    close(): void {
        this.dialogRef.close();
    }
}